import { useCallback, useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import styled from 'styled-components';

import { useSettings } from '../context/settingsContext';
import { partnersConfig } from '../utils/partners';
import PartnerItem from './PartnerItem';
import Icon from './Icon';

type PartnersPanelProps = {
  onSignOut: any;
};

type TooltipOptions = {
  content: JSX.Element | null;
  show: boolean;
  offsetTop: number;
};

const MenuItemWithTooltip = ({
  label,
  to,
  iconName,
  setTooltipOptions,
}: {
  label: string;
  to: string;
  iconName: React.ComponentProps<typeof Icon>['iconName'];
  setTooltipOptions: (options: TooltipOptions) => void;
}) => {
  const itemRef = useRef<HTMLDivElement>(null);

  const handleMouseEnter = () => {
    if (itemRef.current) {
      const rect = itemRef.current.getBoundingClientRect();
      setTooltipOptions({
        show: true,
        content: <span>{label}</span>,
        offsetTop: rect.top + itemRef.current.clientHeight / 2,
      });
    }
  };

  const handleMouseLeave = () => {
    setTooltipOptions({ show: false, content: null, offsetTop: 0 });
  };

  return (
    <MenuItem onMouseEnter={handleMouseEnter} onMouseLeave={handleMouseLeave}>
      <Link to={to}>
        <IconWrapper ref={itemRef}>
          <Icon iconName={iconName} color='#fc8e49' />
        </IconWrapper>
      </Link>
    </MenuItem>
  );
};

type SignOutMenuItemProps = {
  setTooltipOptions: (options: TooltipOptions) => void;
  onSignOut: () => void;
};

const SignOutMenuItem = ({
  setTooltipOptions,
  onSignOut,
}: SignOutMenuItemProps) => {
  const itemRef = useRef<HTMLDivElement>(null);

  const handleMouseEnter = () => {
    if (itemRef.current) {
      const rect = itemRef.current.getBoundingClientRect();
      setTooltipOptions({
        show: true,
        content: <span>Sign out</span>,
        offsetTop: rect.top + itemRef.current.clientHeight / 2,
      });
    }
  };

  const handleMouseLeave = () => {
    setTooltipOptions({ show: false, content: null, offsetTop: 0 });
  };

  return (
    <MenuItem onMouseEnter={handleMouseEnter} onMouseLeave={handleMouseLeave}>
      <SignOutWrapper ref={itemRef}>
        <SignOutButton onClick={onSignOut}>
          <Icon iconName='faRightFromBracket' color='#ff4747' />
        </SignOutButton>
      </SignOutWrapper>
    </MenuItem>
  );
};

const PartnersPanel = ({ onSignOut }: PartnersPanelProps) => {
  const { partners: partnersFromContext } = useSettings();
  const partners = partnersFromContext ?? [];
  const [hasPartnersHiddenAbove, setHasPartnersHiddenAbove] = useState<number | null>(null);
  const [hasPartnersHiddenBelow, setHasPartnersHiddenBelow] = useState<number | null>(null);
  const [tooltipOptions, setTooltipOptions] = useState<{
    content: JSX.Element | null;
    offsetTop: number;
    show: boolean;
  }>({
    show: false,
    content: null,
    offsetTop: 0,
  });
  const [partnerHasOpenItems, setPartnerHasOpenItems] = useState<{
    [index: number]: boolean;
  }>([]);

  const partnerListRef = useRef<Array<HTMLLIElement | null>>([]);

  const checkIfHiddenAbove = useCallback(() => {
    const shouldShow = partnerListRef.current.some((item, index) => {
      if (partnerHasOpenItems[index] && (item?.getBoundingClientRect().top || 0) < 0) {
        setHasPartnersHiddenAbove(index);
        return true;
      }

      return false;
    });

    !shouldShow && setHasPartnersHiddenAbove(null);
  }, [partnerHasOpenItems]);

  const checkIfHiddenBelow = useCallback(() => {
    const partnerListRefReversed = [...partnerListRef.current].reverse();

    const { length } = partnerListRefReversed;

    const shouldShow = partnerListRefReversed.some((item, index) => {
      const originalIndex = length - index - 1;
      if (partnerHasOpenItems[originalIndex] && (item?.getBoundingClientRect().bottom || 0) > window.innerHeight) {
        setHasPartnersHiddenBelow(originalIndex);
        return true;
      }

      return false;
    });

    !shouldShow && setHasPartnersHiddenBelow(null);
  }, [partnerHasOpenItems]);

  useEffect(() => {
    const handleWindowResize = () => {
      if (partnerListRef.current) {
        checkIfHiddenAbove();
        checkIfHiddenBelow();
      }
    };

    window.addEventListener('resize', handleWindowResize);

    return () => window.removeEventListener('resize', handleWindowResize);
  }, [checkIfHiddenAbove, checkIfHiddenBelow]);

  useEffect(() => {
    if (partnerListRef.current) {
      checkIfHiddenAbove();
      checkIfHiddenBelow();
    }
  }, [checkIfHiddenAbove, checkIfHiddenBelow]);

  const handleScroll = () => {
    if (partnerListRef.current) {
      checkIfHiddenAbove();
      checkIfHiddenBelow();
    }
  };

  const handleSetHasOpenItems = (index: number, hasOpenItems: boolean) => {
    setPartnerHasOpenItems(state => ({
      ...state,
      [index]: hasOpenItems,
    }));
  };

  const handleScrollUpClick = () => {
    partnerListRef.current && hasPartnersHiddenAbove !== null && partnerListRef.current[hasPartnersHiddenAbove]?.scrollIntoView();
  };

  const handleScrollDownClick = () => {
    partnerListRef.current && hasPartnersHiddenBelow !== null && partnerListRef.current[hasPartnersHiddenBelow]?.scrollIntoView();
  };

  return (
    <>
      <Nav onScroll={handleScroll}>
        <PartnerList>
          <MenuItemWithTooltip
            label='Search'
            to='/search'
            iconName='faMagnifyingGlass'
            setTooltipOptions={setTooltipOptions}
          />
          <MenuItemWithTooltip
            label='Payments'
            to='/payments'
            iconName='faFileInvoiceDollar'
            setTooltipOptions={setTooltipOptions}
          />
          <MenuItemWithTooltip
            label='E-mail templates'
            to='/templates'
            iconName='faClone'
            setTooltipOptions={setTooltipOptions}
          />
          <MenuItemWithTooltip
            label='Assignments'
            to='/assignments'
            iconName='faListCheck'
            setTooltipOptions={setTooltipOptions}
          />
          <MenuItemWithTooltip
            label='Reports'
            to='/reports'
            iconName='faChartLine'
            setTooltipOptions={setTooltipOptions}
          />
          <ListDivider />
          {partners.map((partner, index) => (
            <PartnerItem
              key={partner.name}
              ref={ref => (partnerListRef.current[index] = ref)}
              index={index}
              partner={partner}
              config={partnersConfig[partner.name] || partnersConfig.default}
              setTooltipOptions={setTooltipOptions}
              setHasOpenItems={handleSetHasOpenItems}
            />
          ))}
          <ListDivider />
          <SignOutMenuItem
            setTooltipOptions={setTooltipOptions}
            onSignOut={onSignOut}
          />
        </PartnerList>
        {hasPartnersHiddenAbove !== null && (
          <ScrollUp onClick={handleScrollUpClick}>
            <Icon iconName='faArrowUp' color='#cfcfcf' />
          </ScrollUp>
        )}
        {hasPartnersHiddenBelow !== null && (
          <ScrollDown onClick={handleScrollDownClick}>
            <Icon iconName='faArrowDown' color='#cfcfcf' />
          </ScrollDown>
        )}
      </Nav>

      <div
        style={{
          position: 'fixed',
          top: 0,
          left: 0,
          width: 0,
          height: 0,
          overflow: 'visible',
          pointerEvents: 'none',
          zIndex: 1000,
          visibility: tooltipOptions.show ? 'visible' : 'hidden',
        }}
      >
        <div
          style={{
            position: 'absolute',
            left: '5rem',
            top: `calc(${tooltipOptions.offsetTop || 100}px - 1rem)`,
            width: 'max-content',
            color: '#fff',
            backgroundColor: 'rgba(0, 0, 0, 0.9)',
            padding: '0.5rem',
            borderRadius: '0.5rem',
            fontSize: '0.8rem',
            fontWeight: 400,
          }}
        >
          {tooltipOptions.content || <span>&nbsp;</span>}
          <div
            style={{
              position: 'absolute',
              top: '50%',
              right: '100%',
              marginTop: '-0.35rem',
              borderWidth: '0.35rem',
              borderStyle: 'solid',
              borderColor: 'transparent rgba(0, 0, 0, 0.9) transparent transparent',
            }}
          />
        </div>
      </div>
    </>
  );
};

export default PartnersPanel;

const Nav = styled.nav`
  position: fixed;
  background-color: #444;
  height: 100vh;
  width: 5rem;
  overflow-y: auto;
  scroll-behavior: smooth;

  ::-webkit-scrollbar {
    width: 0.5rem;
  }
  ::-webkit-scrollbar-track {
    opacity: 0;
  }
  ::-webkit-scrollbar-thumb {
    background-color: #747373;
    opacity: 0.5;
    border-radius: 0.938rem;
    height: 2px;
    width: thin;
  }
  ::-webkit-scrollbar-thumb:hover {
    background-color: #929292;
    max-height: 10px;
    opacity: 0.7;
  }
`;

const PartnerList = styled.ul`
  width: 100%;
  list-style: none;
  padding: 0;
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
  align-items: center;
  margin: 1rem 0;
  position: relative;
`;

const MenuItem = styled.li`
  position: relative;
  transform: scale(1);
  transition: 0.2s linear;

  cursor: pointer;

  img {
    width: 2rem;
    height: 2rem;
  }

  &:hover {
    transform: scale(1.2);
  }
`;

const ListDivider = styled.li`
  border-bottom: 0.0625rem solid #cfcfcf;
  opacity: 0.6;
  width: 3rem;
`;

const IconWrapper = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  font-size: 1.8rem;
  width: 2.8rem;
  height: 2.8rem;
  border: 0.125rem solid #fc8e49;
  border-radius: 0.5rem;
  background-image: linear-gradient(to bottom, rgba(253, 192, 155, 0), rgba(253, 192, 155, 0.3));
`;

const SignOutWrapper = styled(IconWrapper)`
  border-color: #ff4747;
  margin-top: 2rem;
`;

const Scroll = styled.div`
  position: fixed;
  left: 1rem;
  display: flex;
  justify-content: center;
  align-items: center;
  width: 2rem;
  height: 2rem;
  background-color: #ff1f1f;
  border-radius: 50%;
  opacity: 0.8;
  cursor: pointer;
  transition: all 0.3s;
  z-index: 10;

  &:hover {
    opacity: 1;
  }
`;

const ScrollUp = styled(Scroll)`
  top: 0.5rem;
`;

const ScrollDown = styled(Scroll)`
  bottom: 0.5rem;
`;

const SignOutButton = styled.button`
  background-color: transparent;
  border: none;
  outline: none;
  font-size: 1.8rem;
  padding: 0;
  margin: 0;
  cursor: pointer;
  display: flex;
  align-items: center;
`;
